
import { useEffect, useRef, useState } from 'react';
import { VolumeX, Volume2 } from 'lucide-react';
import Player from '@vimeo/player';

const HeroVideo = () => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoad) {
            setShouldLoad(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (videoRef.current && shouldLoad) {
      const player = new Player(videoRef.current, {
        id: 1090289700,
        width: 640,
        height: 360,
        autoplay: true,
        muted: true,
        background: true,
        controls: false,
        loop: true,
        responsive: true,
        // Privacy-enhanced settings
        dnt: true, // Do Not Track mode
        title: false, // Don't show video title
        byline: false, // Don't show video byline
        portrait: false, // Don't show user portrait
        transparent: false, // Disable transparent mode for better privacy
        pip: false, // Disable picture-in-picture
        keyboard: false, // Disable keyboard controls
        autopause: false, // Don't pause when another video plays
        quality: 'auto' // Let Vimeo choose quality automatically
      });

      playerRef.current = player;

      player.ready().then(() => {
        setIsLoaded(true);
      }).catch((error) => {
        console.log('Vimeo player error:', error);
      });

      return () => {
        if (playerRef.current) {
          playerRef.current.destroy();
        }
      };
    }
  }, [shouldLoad]);

  const toggleMute = async () => {
    if (playerRef.current) {
      try {
        if (isMuted) {
          await playerRef.current.setVolume(1);
          setIsMuted(false);
        } else {
          await playerRef.current.setVolume(0);
          setIsMuted(true);
        }
      } catch (error) {
        console.log('Error toggling volume:', error);
      }
    }
  };

  return (
    <div ref={containerRef} className="relative animate-fade-in" style={{ animationDelay: '1000ms' }}>
      <div className="relative z-10">
        <div className="relative w-full h-0 pb-[56.25%] rounded-2xl overflow-hidden shadow-2xl group cursor-pointer" onClick={toggleMute}>
          {shouldLoad ? (
            <>
              <div ref={videoRef} className="absolute top-0 left-0 w-full h-full"></div>
              
              {/* Click to unmute overlay */}
              {isMuted && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-black/70 px-4 py-2 rounded-lg text-white text-sm font-medium">
                    CLICK TO UNMUTE
                  </div>
                </div>
              )}

              {/* Mute/Unmute icon overlay */}
              <div className="absolute top-4 right-4 z-20 bg-black/50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {isMuted ? (
                  <VolumeX className="h-5 w-5 text-white" />
                ) : (
                  <Volume2 className="h-5 w-5 text-white" />
                )}
              </div>

              {/* Click anywhere overlay */}
              <div className="absolute inset-0 z-10"></div>
            </>
          ) : (
            /* Loading placeholder */
            <div className="absolute top-0 left-0 w-full h-full bg-gray-200 flex items-center justify-center">
              <div className="text-gray-500 text-sm">Loading video...</div>
            </div>
          )}
        </div>
      </div>
      <div className="absolute -top-4 -right-4 w-24 h-24 bg-nexx-green/10 rounded-full"></div>
      <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-green-100 rounded-full"></div>
    </div>
  );
};

export default HeroVideo;
